// Multilingual translations for CentrixPay - European Fintech
export const translations = {
  en: {
    nav: {
      home: "Home",
      services: "Services",
      pricing: "Pricing",
      contact: "Contact",
      bookCall: "Book a Call"
    },
    hero: {
      badge: "Trusted Across Europe",
      title: "Save BIG on Payment Processing",
      subtitle: "Join hundreds of European businesses lowering payment fees with transparent pricing and a better payment experience. No hidden fees. No surprises.",
      cta1: "Book a Call",
      cta2: "View Services",
      cardAmount: "€247.83",
      currency: "EUR",
      connected: "Connected",
      cardNumber: "•••• •••• •••• 1234",
      expires: "12/25",
      processing: "Processing...",
      approved: "Approved",
      settled: "Settled"
    },
    features: {
      title: "Why European Businesses Choose CentrixPay",
      subtitle: "Experience the future of payment processing with our innovative solutions designed specifically for the European market.",
      imageAlt: "European business owner welcoming customers",
      imageCaption: "Supporting European entrepreneurs with transparent, reliable payment solutions",
      security: {
        title: "Bank-Level Security",
        description: "PCI DSS Level 1 compliant with end-to-end encryption. Full GDPR compliance and PSD2 ready."
      },
      settlement: {
        title: "Next Day Settlements",
        description: "Get your money faster with SEPA integration and instant notifications across the Eurozone."
      },
      support: {
        title: "24/7 European Support",
        description: "Real people, real solutions. Our multilingual European support team is always here to help."
      }
    },
    testimonials: {
      title: "Trusted by European Businesses",
      subtitle: "From small retailers to growing enterprises, businesses across Europe save with CentrixPay",
      imageAlt: "Happy European business owners",
      imageCaption: "Building success together - European entrepreneurs thriving with streamlined payment solutions",
      reviews: [
        {
          name: "Anna Novotná",
          role: "Owner, Café in Prague, CZ",
          quote: "CentrixPay saved us over €3,000 in our first year. The transparency is incredible.",
          image: "testimonial1"
        },
        {
          name: "Markus Weber",
          role: "Owner, Auto Repair Shop, Berlin DE",
          quote: "The next-day settlements have revolutionized our cash flow management.",
          image: "testimonial2"
        },
        {
          name: "Carlos García",
          role: "Owner, Electronics Store, Madrid ES",
          quote: "Best decision we made for our business. No hidden fees, just honest pricing.",
          image: "testimonial3"
        }
      ]
    },
    cta: {
      title: "Ready to Start Saving?",
      subtitle: "Join hundreds of European businesses that have already made the switch to transparent, fair pricing and saved thousands.",
      button1: "Get Started Today",
      button2: "View Pricing"
    },
    footer: {
      description: "Premium payment processing for European businesses. SEPA integrated, PSD2 compliant, GDPR secure.",
      services: "Services",
      company: "Company",
      legal: "Legal",
      about: "About Us",
      careers: "Careers",
      blog: "Blog",
      privacy: "Privacy Policy",
      terms: "Terms of Service",
      cookies: "Cookie Policy",
      contact: "Contact",
      copyright: "© 2025 CentrixPay. All rights reserved."
    },
    services: {
      hero: {
        title: "Payment Solutions Built for Europe",
        subtitle: "Comprehensive payment processing designed for the unique needs of European businesses."
      },
      cards: [
        {
          title: "Card Processing",
          description: "Accept all major credit and debit cards with competitive interchange-plus pricing. Visa, Mastercard, Maestro, and more.",
          icon: "CreditCard"
        },
        {
          title: "SEPA Payments",
          description: "Fast and secure SEPA Direct Debit and Credit Transfer for seamless Euro transactions across 36 countries.",
          icon: "Building"
        },
        {
          title: "Multi-Currency",
          description: "Process payments in EUR, CZK, PLN, and more. Dynamic currency conversion for international customers.",
          icon: "Globe"
        },
        {
          title: "E-Commerce Integration",
          description: "Seamless integration with your online store. Plugins for Shopify, WooCommerce, Magento, and custom APIs.",
          icon: "ShoppingCart"
        },
        {
          title: "POS Terminals",
          description: "Modern payment terminals for brick-and-mortar stores. Contactless, chip, and PIN supported.",
          icon: "Smartphone"
        },
        {
          title: "Recurring Billing",
          description: "Subscription management and recurring payment solutions for SaaS and membership businesses.",
          icon: "RefreshCw"
        }
      ],
      compliance: {
        title: "European Compliance Built-In",
        items: [
          { title: "PSD2 Ready", description: "Strong Customer Authentication (SCA) compliant" },
          { title: "GDPR Secure", description: "Full data protection and privacy compliance" },
          { title: "PCI DSS Level 1", description: "Highest level of payment security certification" }
        ]
      }
    },
    pricing: {
      hero: {
        title: "Pricing That Works for You",
        subtitle: "Transparent, competitive rates tailored to your business. No hidden fees, no surprises—just honest pricing."
      },
      getQuote: "Get Your Custom Quote",
      quoteCard: {
        customQuote: "Custom Quote",
        tailoredForYou: "TAILORED FOR YOU",
        transparentPricing: "Transparent Pricing",
        nextDaySettlements: "Next-Day Settlements",
        dedicatedSupport: "Dedicated Support",
        hiddenFees: "Hidden Fees",
        included: "Included",
        yourRate: "Your Rate",
        competitive: "Competitive",
        basedOnVolume: "Based on your volume",
        lowerFees: "Lower Fees"
      },
      valueProps: {
        title: "Why Businesses Choose Us",
        subtitle: "We believe in earning your business through value, not locking you into contracts.",
        items: [
          { title: "Complete Transparency", description: "See exactly what you're paying for. Every fee is clearly listed on your statement—no surprises, ever." },
          { title: "Competitive Rates", description: "We work with you to find the best rate for your business volume and industry. Fair pricing, always." },
          { title: "No Hidden Fees", description: "No setup fees, no cancellation fees, no monthly minimums. Just straightforward payment processing." },
          { title: "Fast Settlements", description: "Get your money when you need it. Next-day and same-day settlement options available." },
          { title: "Simple Integration", description: "Easy setup with your existing systems. We handle the technical complexity so you don't have to." },
          { title: "Dedicated Support", description: "Real people who understand your business. European-based support team available when you need them." }
        ]
      },
      comparison: {
        title: "How We're Different",
        subtitle: "See how CentrixPay compares to traditional payment processors.",
        feature: "Feature",
        traditional: "Traditional",
        features: [
          "Transparent fee structure",
          "No long-term contracts",
          "Next-day settlements",
          "No hidden monthly fees",
          "Dedicated account manager",
          "Multi-currency support",
          "PSD2 & GDPR compliant",
          "24/7 European support"
        ]
      },
      ctaSection: {
        title: "Ready to See What You Could Save?",
        subtitle: "Get a personalized quote based on your business needs. No commitment, no pressure—just honest numbers.",
        callUs: "Or Call Us Directly"
      },
      plans: [
        {
          name: "Starter",
          price: "Simple",
          priceNote: "Straightforward pricing",
          description: "Perfect for small businesses ready to accept payments with ease",
          features: [
            "All major cards accepted",
            "SEPA payments included",
            "Standard POS terminal",
            "Email support",
            "Monthly statements"
          ],
          cta: "Get Started",
          popular: false
        },
        {
          name: "Professional",
          price: "Optimized",
          priceNote: "Best value for growing businesses",
          description: "Ideal for scaling businesses that need more power and flexibility",
          features: [
            "Everything in Starter",
            "Next-day settlements",
            "Advanced POS terminal",
            "Priority phone support",
            "Real-time analytics",
            "Multi-currency processing"
          ],
          cta: "Most Popular",
          popular: true
        },
        {
          name: "Enterprise",
          price: "Custom",
          priceNote: "Tailored to your needs",
          description: "For large businesses with high transaction volumes and unique requirements",
          features: [
            "Everything in Professional",
            "Same-day settlements",
            "Dedicated account manager",
            "Custom integrations",
            "SLA guarantee",
            "White-label options"
          ],
          cta: "Contact Sales",
          popular: false
        }
      ],
      faq: {
        title: "Frequently Asked Questions",
        items: [
          {
            question: "What is interchange-plus pricing?",
            answer: "Interchange-plus pricing means you pay the actual card network fee (interchange) plus a small, transparent markup. This is the most honest pricing model in the industry."
          },
          {
            question: "Are there any hidden fees?",
            answer: "Absolutely not. We believe in complete transparency. You'll see every fee clearly on your monthly statement. No surprises, ever."
          },
          {
            question: "How fast are settlements?",
            answer: "Standard settlements are T+2 business days. Professional plans get next-day settlements, and Enterprise plans can receive same-day settlements."
          },
          {
            question: "Do you support recurring payments?",
            answer: "Yes! All plans include support for recurring billing and subscription management. Perfect for SaaS and membership businesses."
          }
        ]
      }
    },
    contact: {
      hero: {
        title: "Let's Talk About Your Business",
        subtitle: "Get a free consultation and see how much you could save with CentrixPay."
      },
      form: {
        name: "Full Name",
        namePlaceholder: "Enter your full name",
        email: "Email Address",
        emailPlaceholder: "your@email.com",
        company: "Company Name",
        companyPlaceholder: "Your company name",
        country: "Country",
        countryPlaceholder: "Select your country",
        volume: "Monthly Transaction Volume",
        volumePlaceholder: "Select volume range",
        message: "Message",
        messagePlaceholder: "Tell us about your business and payment needs...",
        submit: "Send Message",
        submitting: "Sending...",
        success: "Thank you! We'll be in touch within 24 hours.",
        error: "Something went wrong. Please try again."
      },
      countries: [
        "Czech Republic",
        "Germany",
        "Poland",
        "Spain",
        "Portugal",
        "Austria",
        "Netherlands",
        "Belgium",
        "France",
        "Italy",
        "Other EU Country"
      ],
      volumes: [
        "€0 - €10,000",
        "€10,000 - €50,000",
        "€50,000 - €100,000",
        "€100,000 - €500,000",
        "€500,000+"
      ],
      info: {
        title: "Why Choose CentrixPay?",
        items: [
          { title: "Save Up to 40%", description: "Our clients typically save 30-40% on payment processing fees." },
          { title: "Quick Setup", description: "Get started in as little as 48 hours with our streamlined onboarding." },
          { title: "No Contracts", description: "Month-to-month billing with no long-term commitments required." }
        ]
      }
    }
  },
  cs: {
    nav: {
      home: "Domů",
      services: "Služby",
      pricing: "Ceník",
      contact: "Kontakt",
      bookCall: "Rezervovat hovor"
    },
    hero: {
      badge: "Důvěryhodní po celé Evropě",
      title: "UŠETŘTE na zpracování plateb",
      subtitle: "Připojte se ke stovkám evropských firem, které snižují poplatky za platby díky transparentním cenám a lepšímu platebnímu zážitku. Žádné skryté poplatky. Žádná překvapení.",
      cta1: "Rezervovat hovor",
      cta2: "Zobrazit služby",
      cardAmount: "247,83 €",
      currency: "EUR",
      connected: "Připojeno",
      cardNumber: "•••• •••• •••• 1234",
      expires: "12/25",
      processing: "Zpracování...",
      approved: "Schváleno",
      settled: "Vypořádáno"
    },
    features: {
      title: "Proč si evropské firmy vybírají CentrixPay",
      subtitle: "Zažijte budoucnost zpracování plateb s našimi inovativními řešeními navrženými speciálně pro evropský trh.",
      imageAlt: "Evropský majitel firmy vítající zákazníky",
      imageCaption: "Podporujeme evropské podnikatele transparentními a spolehlivými platebními řešeními",
      security: {
        title: "Bankovní úroveň zabezpečení",
        description: "PCI DSS Level 1 s end-to-end šifrováním. Plný soulad s GDPR a připravenost na PSD2."
      },
      settlement: {
        title: "Vypořádání do druhého dne",
        description: "Získejte své peníze rychleji díky integraci SEPA a okamžitým notifikacím po celé Eurozóně."
      },
      support: {
        title: "24/7 evropská podpora",
        description: "Skuteční lidé, skutečná řešení. Náš vícejazyčný evropský tým podpory je vždy k dispozici."
      }
    },
    testimonials: {
      title: "Důvěřují nám evropské firmy",
      subtitle: "Od malých obchodníků po rostoucí podniky, firmy po celé Evropě šetří s CentrixPay",
      imageAlt: "Spokojení evropští majitelé firem",
      imageCaption: "Budujeme úspěch společně - evropští podnikatelé prosperující s efektivními platebními řešeními",
      reviews: [
        {
          name: "Anna Novotná",
          role: "Majitelka kavárny, Praha",
          quote: "CentrixPay nám v prvním roce ušetřil přes 3 000 €. Transparentnost je neuvěřitelná.",
          image: "testimonial1"
        },
        {
          name: "Markus Weber",
          role: "Majitel autoservisu, Berlín",
          quote: "Vypořádání do druhého dne změnilo naše řízení cash flow.",
          image: "testimonial2"
        },
        {
          name: "Carlos García",
          role: "Majitel obchodu s elektronikou, Madrid",
          quote: "Nejlepší rozhodnutí pro naši firmu. Žádné skryté poplatky, jen férové ceny.",
          image: "testimonial3"
        }
      ]
    },
    cta: {
      title: "Připraveni začít šetřit?",
      subtitle: "Připojte se ke stovkám evropských firem, které již přešly na transparentní, férové ceny a ušetřily tisíce.",
      button1: "Začněte ještě dnes",
      button2: "Zobrazit ceník"
    },
    footer: {
      description: "Prémiové zpracování plateb pro evropské firmy. Integrované SEPA, v souladu s PSD2, zabezpečené GDPR.",
      services: "Služby",
      company: "Společnost",
      legal: "Právní",
      about: "O nás",
      careers: "Kariéra",
      blog: "Blog",
      privacy: "Zásady ochrany osobních údajů",
      terms: "Obchodní podmínky",
      cookies: "Zásady cookies",
      contact: "Kontakt",
      copyright: "© 2025 CentrixPay. Všechna práva vyhrazena."
    },
    services: {
      hero: {
        title: "Platební řešení pro Evropu",
        subtitle: "Komplexní zpracování plateb navržené pro jedinečné potřeby evropských firem."
      },
      cards: [
        {
          title: "Zpracování karet",
          description: "Přijímejte všechny hlavní kreditní a debetní karty s konkurenčními cenami interchange-plus. Visa, Mastercard, Maestro a další.",
          icon: "CreditCard"
        },
        {
          title: "SEPA platby",
          description: "Rychlé a bezpečné SEPA inkaso a převody pro bezproblémové eurové transakce ve 36 zemích.",
          icon: "Building"
        },
        {
          title: "Více měn",
          description: "Zpracovávejte platby v EUR, CZK, PLN a dalších. Dynamická konverze měn pro mezinárodní zákazníky.",
          icon: "Globe"
        },
        {
          title: "E-commerce integrace",
          description: "Bezproblémová integrace s vaším online obchodem. Pluginy pro Shopify, WooCommerce, Magento a vlastní API.",
          icon: "ShoppingCart"
        },
        {
          title: "POS terminály",
          description: "Moderní platební terminály pro kamenné obchody. Bezkontaktní, čipové a PIN platby.",
          icon: "Smartphone"
        },
        {
          title: "Opakované platby",
          description: "Správa předplatného a řešení opakovaných plateb pro SaaS a členské firmy.",
          icon: "RefreshCw"
        }
      ],
      compliance: {
        title: "Evropský soulad zabudovaný",
        items: [
          { title: "PSD2 Ready", description: "V souladu se silnou autentizací zákazníka (SCA)" },
          { title: "GDPR Secure", description: "Plný soulad s ochranou dat a soukromím" },
          { title: "PCI DSS Level 1", description: "Nejvyšší úroveň certifikace platební bezpečnosti" }
        ]
      }
    },
    pricing: {
      hero: {
        title: "Ceny, které vám vyhovují",
        subtitle: "Transparentní, konkurenční sazby přizpůsobené vašemu podnikání. Žádné skryté poplatky, žádná překvapení."
      },
      getQuote: "Získejte individuální nabídku",
      quoteCard: {
        customQuote: "Individuální nabídka",
        tailoredForYou: "PŘIZPŮSOBENO VÁM",
        transparentPricing: "Transparentní ceny",
        nextDaySettlements: "Vypořádání do druhého dne",
        dedicatedSupport: "Vyhrazená podpora",
        hiddenFees: "Skryté poplatky",
        included: "Zahrnuto",
        yourRate: "Vaše sazba",
        competitive: "Konkurenční",
        basedOnVolume: "Na základě vašeho objemu",
        lowerFees: "Nižší poplatky"
      },
      valueProps: {
        title: "Proč si nás firmy vybírají",
        subtitle: "Věříme v získání vašeho podnikání hodnotou, ne uzamčením do smluv.",
        items: [
          { title: "Úplná transparentnost", description: "Přesně vidíte, za co platíte. Každý poplatek je jasně uveden na výpisu – žádná překvapení." },
          { title: "Konkurenční sazby", description: "Spolupracujeme s vámi na nalezení nejlepší sazby pro váš objem a odvětví. Férové ceny, vždy." },
          { title: "Žádné skryté poplatky", description: "Žádné poplatky za zřízení, zrušení ani měsíční minima. Jen přímočaré zpracování plateb." },
          { title: "Rychlé vypořádání", description: "Získejte peníze, když je potřebujete. K dispozici jsou možnosti vypořádání do druhého dne i ve stejný den." },
          { title: "Jednoduchá integrace", description: "Snadné nastavení s vašimi stávajícími systémy. Technickou složitost řešíme za vás." },
          { title: "Vyhrazená podpora", description: "Skuteční lidé, kteří rozumí vašemu podnikání. Evropský tým podpory k dispozici, když potřebujete." }
        ]
      },
      comparison: {
        title: "Čím se lišíme",
        subtitle: "Podívejte se, jak se CentrixPay srovnává s tradičními zpracovateli plateb.",
        feature: "Funkce",
        traditional: "Tradiční",
        features: [
          "Transparentní struktura poplatků",
          "Žádné dlouhodobé smlouvy",
          "Vypořádání do druhého dne",
          "Žádné skryté měsíční poplatky",
          "Vyhrazený account manager",
          "Podpora více měn",
          "Soulad s PSD2 a GDPR",
          "24/7 evropská podpora"
        ]
      },
      ctaSection: {
        title: "Připraveni zjistit, kolik můžete ušetřit?",
        subtitle: "Získejte personalizovanou nabídku na základě potřeb vašeho podnikání. Žádný závazek, žádný tlak – jen poctivá čísla.",
        callUs: "Nebo nám zavolejte"
      },
      plans: [
        {
          name: "Starter",
          price: "Jednoduché",
          priceNote: "Přímočaré ceny",
          description: "Ideální pro malé firmy připravené přijímat platby",
          features: [
            "Všechny hlavní karty",
            "SEPA platby zahrnuty",
            "Standardní POS terminál",
            "E-mailová podpora",
            "Měsíční výpisy"
          ],
          cta: "Začít",
          popular: false
        },
        {
          name: "Professional",
          price: "Optimalizované",
          priceNote: "Nejlepší hodnota pro rostoucí firmy",
          description: "Ideální pro rostoucí firmy s vyššími objemy",
          features: [
            "Vše ze Starter",
            "Vypořádání do druhého dne",
            "Pokročilý POS terminál",
            "Prioritní telefonická podpora",
            "Analytika v reálném čase",
            "Zpracování více měn"
          ],
          cta: "Nejoblíbenější",
          popular: true
        },
        {
          name: "Enterprise",
          price: "Na míru",
          priceNote: "Přizpůsobeno vašim potřebám",
          description: "Pro velké firmy s vysokými objemy transakcí",
          features: [
            "Vše z Professional",
            "Vypořádání ve stejný den",
            "Dedikovaný account manager",
            "Vlastní integrace",
            "SLA garance",
            "White-label možnosti"
          ],
          cta: "Kontaktovat prodej",
          popular: false
        }
      ],
      faq: {
        title: "Často kladené otázky",
        items: [
          {
            question: "Co je interchange-plus cena?",
            answer: "Interchange-plus cena znamená, že platíte skutečný poplatek kartové sítě (interchange) plus malou, transparentní přirážku. Je to nejférovější cenový model v odvětví."
          },
          {
            question: "Jsou nějaké skryté poplatky?",
            answer: "Rozhodně ne. Věříme v úplnou transparentnost. Každý poplatek uvidíte jasně na měsíčním výpisu. Žádná překvapení."
          },
          {
            question: "Jak rychlé jsou vypořádání?",
            answer: "Standardní vypořádání jsou T+2 pracovní dny. Professional plány mají vypořádání do druhého dne a Enterprise plány mohou získat vypořádání ve stejný den."
          },
          {
            question: "Podporujete opakované platby?",
            answer: "Ano! Všechny plány zahrnují podporu opakovaných plateb a správy předplatného. Ideální pro SaaS a členské firmy."
          }
        ]
      }
    },
    contact: {
      hero: {
        title: "Pojďme mluvit o vašem podnikání",
        subtitle: "Získejte bezplatnou konzultaci a zjistěte, kolik můžete ušetřit s CentrixPay."
      },
      form: {
        name: "Celé jméno",
        namePlaceholder: "Zadejte své celé jméno",
        email: "E-mailová adresa",
        emailPlaceholder: "vas@email.cz",
        company: "Název společnosti",
        companyPlaceholder: "Název vaší společnosti",
        country: "Země",
        countryPlaceholder: "Vyberte svou zemi",
        volume: "Měsíční objem transakcí",
        volumePlaceholder: "Vyberte rozsah objemu",
        message: "Zpráva",
        messagePlaceholder: "Řekněte nám o svém podnikání a platebních potřebách...",
        submit: "Odeslat zprávu",
        submitting: "Odesílání...",
        success: "Děkujeme! Ozveme se do 24 hodin.",
        error: "Něco se pokazilo. Zkuste to prosím znovu."
      },
      countries: [
        "Česká republika",
        "Německo",
        "Polsko",
        "Španělsko",
        "Portugalsko",
        "Rakousko",
        "Nizozemsko",
        "Belgie",
        "Francie",
        "Itálie",
        "Jiná země EU"
      ],
      volumes: [
        "0 € - 10 000 €",
        "10 000 € - 50 000 €",
        "50 000 € - 100 000 €",
        "100 000 € - 500 000 €",
        "500 000 €+"
      ],
      info: {
        title: "Proč si vybrat CentrixPay?",
        items: [
          { title: "Ušetřete až 40%", description: "Naši klienti typicky ušetří 30-40% na poplatcích za zpracování plateb." },
          { title: "Rychlé nastavení", description: "Začněte již za 48 hodin díky našemu efektivnímu onboardingu." },
          { title: "Žádné smlouvy", description: "Měsíční fakturace bez dlouhodobých závazků." }
        ]
      }
    }
  },
  de: {
    nav: {
      home: "Startseite",
      services: "Leistungen",
      pricing: "Preise",
      contact: "Kontakt",
      bookCall: "Termin buchen"
    },
    hero: {
      badge: "In ganz Europa vertraut",
      title: "SPAREN Sie bei der Zahlungsabwicklung",
      subtitle: "Schließen Sie sich Hunderten europäischer Unternehmen an, die Zahlungsgebühren mit transparenten Preisen und einem besseren Zahlungserlebnis senken. Keine versteckten Gebühren. Keine Überraschungen.",
      cta1: "Termin buchen",
      cta2: "Leistungen ansehen",
      cardAmount: "247,83 €",
      currency: "EUR",
      connected: "Verbunden",
      cardNumber: "•••• •••• •••• 1234",
      expires: "12/25",
      processing: "Verarbeitung...",
      approved: "Genehmigt",
      settled: "Abgerechnet"
    },
    features: {
      title: "Warum europäische Unternehmen CentrixPay wählen",
      subtitle: "Erleben Sie die Zukunft der Zahlungsabwicklung mit unseren innovativen Lösungen, die speziell für den europäischen Markt entwickelt wurden.",
      imageAlt: "Europäischer Geschäftsinhaber begrüßt Kunden",
      imageCaption: "Wir unterstützen europäische Unternehmer mit transparenten, zuverlässigen Zahlungslösungen",
      security: {
        title: "Sicherheit auf Bankniveau",
        description: "PCI DSS Level 1 konform mit Ende-zu-Ende-Verschlüsselung. Vollständige GDPR-Konformität und PSD2-bereit."
      },
      settlement: {
        title: "Auszahlung am nächsten Tag",
        description: "Erhalten Sie Ihr Geld schneller mit SEPA-Integration und sofortigen Benachrichtigungen in der gesamten Eurozone."
      },
      support: {
        title: "24/7 Europäischer Support",
        description: "Echte Menschen, echte Lösungen. Unser mehrsprachiges europäisches Support-Team ist immer für Sie da."
      }
    },
    testimonials: {
      title: "Von europäischen Unternehmen vertraut",
      subtitle: "Von kleinen Händlern bis zu wachsenden Unternehmen - Firmen in ganz Europa sparen mit CentrixPay",
      imageAlt: "Zufriedene europäische Geschäftsinhaber",
      imageCaption: "Gemeinsam Erfolg aufbauen - Europäische Unternehmer florieren mit optimierten Zahlungslösungen",
      reviews: [
        {
          name: "Anna Novotná",
          role: "Inhaberin, Café in Prag, CZ",
          quote: "CentrixPay hat uns im ersten Jahr über 3.000 € gespart. Die Transparenz ist unglaublich.",
          image: "testimonial1"
        },
        {
          name: "Markus Weber",
          role: "Inhaber, Autowerkstatt, Berlin DE",
          quote: "Die Auszahlung am nächsten Tag hat unser Cashflow-Management revolutioniert.",
          image: "testimonial2"
        },
        {
          name: "Carlos García",
          role: "Inhaber, Elektronikgeschäft, Madrid ES",
          quote: "Die beste Entscheidung für unser Unternehmen. Keine versteckten Gebühren, nur ehrliche Preise.",
          image: "testimonial3"
        }
      ]
    },
    cta: {
      title: "Bereit zum Sparen?",
      subtitle: "Schließen Sie sich Hunderten europäischer Unternehmen an, die bereits auf transparente, faire Preise umgestellt haben und Tausende gespart haben.",
      button1: "Heute starten",
      button2: "Preise ansehen"
    },
    footer: {
      description: "Premium-Zahlungsabwicklung für europäische Unternehmen. SEPA-integriert, PSD2-konform, GDPR-sicher.",
      services: "Leistungen",
      company: "Unternehmen",
      legal: "Rechtliches",
      about: "Über uns",
      careers: "Karriere",
      blog: "Blog",
      privacy: "Datenschutz",
      terms: "AGB",
      cookies: "Cookie-Richtlinie",
      contact: "Kontakt",
      copyright: "© 2025 CentrixPay. Alle Rechte vorbehalten."
    },
    services: {
      hero: {
        title: "Zahlungslösungen für Europa",
        subtitle: "Umfassende Zahlungsabwicklung für die besonderen Bedürfnisse europäischer Unternehmen."
      },
      cards: [
        {
          title: "Kartenabwicklung",
          description: "Akzeptieren Sie alle gängigen Kredit- und Debitkarten mit wettbewerbsfähigen Interchange-Plus-Preisen. Visa, Mastercard, Maestro und mehr.",
          icon: "CreditCard"
        },
        {
          title: "SEPA-Zahlungen",
          description: "Schnelle und sichere SEPA-Lastschriften und Überweisungen für nahtlose Euro-Transaktionen in 36 Ländern.",
          icon: "Building"
        },
        {
          title: "Multi-Währung",
          description: "Verarbeiten Sie Zahlungen in EUR, CZK, PLN und mehr. Dynamische Währungsumrechnung für internationale Kunden.",
          icon: "Globe"
        },
        {
          title: "E-Commerce-Integration",
          description: "Nahtlose Integration mit Ihrem Online-Shop. Plugins für Shopify, WooCommerce, Magento und individuelle APIs.",
          icon: "ShoppingCart"
        },
        {
          title: "POS-Terminals",
          description: "Moderne Zahlungsterminals für stationäre Geschäfte. Kontaktlos, Chip und PIN unterstützt.",
          icon: "Smartphone"
        },
        {
          title: "Wiederkehrende Zahlungen",
          description: "Abonnementverwaltung und wiederkehrende Zahlungslösungen für SaaS- und Mitgliedschaftsunternehmen.",
          icon: "RefreshCw"
        }
      ],
      compliance: {
        title: "Europäische Compliance eingebaut",
        items: [
          { title: "PSD2 Ready", description: "Konform mit starker Kundenauthentifizierung (SCA)" },
          { title: "GDPR Secure", description: "Vollständige Datenschutz-Compliance" },
          { title: "PCI DSS Level 1", description: "Höchste Zahlungssicherheitszertifizierung" }
        ]
      }
    },
    pricing: {
      hero: {
        title: "Preise, die für Sie funktionieren",
        subtitle: "Transparente, wettbewerbsfähige Tarife für Ihr Unternehmen. Keine versteckten Gebühren, keine Überraschungen."
      },
      getQuote: "Individuelles Angebot anfordern",
      quoteCard: {
        customQuote: "Individuelles Angebot",
        tailoredForYou: "AUF SIE ZUGESCHNITTEN",
        transparentPricing: "Transparente Preise",
        nextDaySettlements: "Auszahlung am nächsten Tag",
        dedicatedSupport: "Dedizierter Support",
        hiddenFees: "Versteckte Gebühren",
        included: "Inklusive",
        yourRate: "Ihr Tarif",
        competitive: "Wettbewerbsfähig",
        basedOnVolume: "Basierend auf Ihrem Volumen",
        lowerFees: "Niedrigere Gebühren"
      },
      valueProps: {
        title: "Warum Unternehmen uns wählen",
        subtitle: "Wir glauben daran, Ihr Geschäft durch Wert zu gewinnen, nicht durch Vertragsbindung.",
        items: [
          { title: "Vollständige Transparenz", description: "Sehen Sie genau, wofür Sie bezahlen. Jede Gebühr ist klar auf Ihrer Abrechnung aufgeführt – keine Überraschungen." },
          { title: "Wettbewerbsfähige Tarife", description: "Wir arbeiten mit Ihnen zusammen, um den besten Tarif für Ihr Volumen und Ihre Branche zu finden. Faire Preise, immer." },
          { title: "Keine versteckten Gebühren", description: "Keine Einrichtungsgebühren, keine Kündigungsgebühren, keine monatlichen Mindestbeträge. Nur unkomplizierte Zahlungsabwicklung." },
          { title: "Schnelle Auszahlungen", description: "Erhalten Sie Ihr Geld, wenn Sie es brauchen. Auszahlungen am nächsten Tag und am selben Tag verfügbar." },
          { title: "Einfache Integration", description: "Einfache Einrichtung mit Ihren bestehenden Systemen. Wir kümmern uns um die technische Komplexität." },
          { title: "Dedizierter Support", description: "Echte Menschen, die Ihr Geschäft verstehen. Europäisches Support-Team, wenn Sie es brauchen." }
        ]
      },
      comparison: {
        title: "Was uns unterscheidet",
        subtitle: "Sehen Sie, wie CentrixPay im Vergleich zu traditionellen Zahlungsanbietern abschneidet.",
        feature: "Funktion",
        traditional: "Traditionell",
        features: [
          "Transparente Gebührenstruktur",
          "Keine langfristigen Verträge",
          "Auszahlung am nächsten Tag",
          "Keine versteckten monatlichen Gebühren",
          "Dedizierter Account Manager",
          "Multi-Währungsunterstützung",
          "PSD2 & GDPR konform",
          "24/7 europäischer Support"
        ]
      },
      ctaSection: {
        title: "Bereit zu sehen, was Sie sparen könnten?",
        subtitle: "Erhalten Sie ein personalisiertes Angebot basierend auf Ihren Geschäftsanforderungen. Keine Verpflichtung, kein Druck – nur ehrliche Zahlen.",
        callUs: "Oder rufen Sie uns direkt an"
      },
      plans: [
        {
          name: "Starter",
          price: "Einfach",
          priceNote: "Unkomplizierte Preise",
          description: "Perfekt für kleine Unternehmen, die bereit sind, Zahlungen zu akzeptieren",
          features: [
            "Alle gängigen Karten",
            "SEPA-Zahlungen inklusive",
            "Standard-POS-Terminal",
            "E-Mail-Support",
            "Monatliche Abrechnungen"
          ],
          cta: "Starten",
          popular: false
        },
        {
          name: "Professional",
          price: "Optimiert",
          priceNote: "Bester Wert für wachsende Unternehmen",
          description: "Ideal für wachsende Unternehmen mit höheren Volumina",
          features: [
            "Alles aus Starter",
            "Auszahlung am nächsten Tag",
            "Erweitertes POS-Terminal",
            "Prioritäts-Telefonsupport",
            "Echtzeit-Analysen",
            "Multi-Währungsverarbeitung"
          ],
          cta: "Am beliebtesten",
          popular: true
        },
        {
          name: "Enterprise",
          price: "Individuell",
          priceNote: "Auf Ihre Bedürfnisse zugeschnitten",
          description: "Für große Unternehmen mit hohen Transaktionsvolumina",
          features: [
            "Alles aus Professional",
            "Auszahlung am selben Tag",
            "Dedizierter Account Manager",
            "Individuelle Integrationen",
            "SLA-Garantie",
            "White-Label-Optionen"
          ],
          cta: "Vertrieb kontaktieren",
          popular: false
        }
      ],
      faq: {
        title: "Häufig gestellte Fragen",
        items: [
          {
            question: "Was ist Interchange-Plus-Preisgestaltung?",
            answer: "Bei der Interchange-Plus-Preisgestaltung zahlen Sie die tatsächliche Kartennetzwerkgebühr (Interchange) plus einen kleinen, transparenten Aufschlag. Das ist das ehrlichste Preismodell der Branche."
          },
          {
            question: "Gibt es versteckte Gebühren?",
            answer: "Absolut nicht. Wir glauben an vollständige Transparenz. Sie sehen jede Gebühr klar auf Ihrer monatlichen Abrechnung. Keine Überraschungen."
          },
          {
            question: "Wie schnell sind die Auszahlungen?",
            answer: "Standardauszahlungen erfolgen T+2 Werktage. Professional-Pläne erhalten Auszahlungen am nächsten Tag, und Enterprise-Pläne können Auszahlungen am selben Tag erhalten."
          },
          {
            question: "Unterstützen Sie wiederkehrende Zahlungen?",
            answer: "Ja! Alle Pläne beinhalten Unterstützung für wiederkehrende Zahlungen und Abonnementverwaltung. Perfekt für SaaS- und Mitgliedschaftsunternehmen."
          }
        ]
      }
    },
    contact: {
      hero: {
        title: "Lassen Sie uns über Ihr Unternehmen sprechen",
        subtitle: "Erhalten Sie eine kostenlose Beratung und erfahren Sie, wie viel Sie mit CentrixPay sparen können."
      },
      form: {
        name: "Vollständiger Name",
        namePlaceholder: "Geben Sie Ihren vollständigen Namen ein",
        email: "E-Mail-Adresse",
        emailPlaceholder: "ihre@email.de",
        company: "Firmenname",
        companyPlaceholder: "Ihr Firmenname",
        country: "Land",
        countryPlaceholder: "Wählen Sie Ihr Land",
        volume: "Monatliches Transaktionsvolumen",
        volumePlaceholder: "Wählen Sie Volumenbereich",
        message: "Nachricht",
        messagePlaceholder: "Erzählen Sie uns von Ihrem Unternehmen und Ihren Zahlungsbedürfnissen...",
        submit: "Nachricht senden",
        submitting: "Wird gesendet...",
        success: "Vielen Dank! Wir melden uns innerhalb von 24 Stunden.",
        error: "Etwas ist schief gelaufen. Bitte versuchen Sie es erneut."
      },
      countries: [
        "Tschechische Republik",
        "Deutschland",
        "Polen",
        "Spanien",
        "Portugal",
        "Österreich",
        "Niederlande",
        "Belgien",
        "Frankreich",
        "Italien",
        "Anderes EU-Land"
      ],
      volumes: [
        "0 € - 10.000 €",
        "10.000 € - 50.000 €",
        "50.000 € - 100.000 €",
        "100.000 € - 500.000 €",
        "500.000 €+"
      ],
      info: {
        title: "Warum CentrixPay wählen?",
        items: [
          { title: "Sparen Sie bis zu 40%", description: "Unsere Kunden sparen typischerweise 30-40% bei Zahlungsabwicklungsgebühren." },
          { title: "Schnelle Einrichtung", description: "Starten Sie in nur 48 Stunden mit unserem optimierten Onboarding." },
          { title: "Keine Verträge", description: "Monatliche Abrechnung ohne langfristige Verpflichtungen." }
        ]
      }
    }
  },
  pl: {
    nav: {
      home: "Strona główna",
      services: "Usługi",
      pricing: "Cennik",
      contact: "Kontakt",
      bookCall: "Umów rozmowę"
    },
    hero: {
      badge: "Zaufani w całej Europie",
      title: "OSZCZĘDZAJ na przetwarzaniu płatności",
      subtitle: "Dołącz do setek europejskich firm obniżających opłaty za płatności dzięki przejrzystym cenom i lepszemu doświadczeniu płatniczemu. Bez ukrytych opłat. Bez niespodzianek.",
      cta1: "Umów rozmowę",
      cta2: "Zobacz usługi",
      cardAmount: "247,83 €",
      currency: "EUR",
      connected: "Połączono",
      cardNumber: "•••• •••• •••• 1234",
      expires: "12/25",
      processing: "Przetwarzanie...",
      approved: "Zatwierdzone",
      settled: "Rozliczone"
    },
    features: {
      title: "Dlaczego europejskie firmy wybierają CentrixPay",
      subtitle: "Poznaj przyszłość przetwarzania płatności dzięki naszym innowacyjnym rozwiązaniom zaprojektowanym specjalnie dla rynku europejskiego.",
      imageAlt: "Europejski właściciel firmy witający klientów",
      imageCaption: "Wspieramy europejskich przedsiębiorców przejrzystymi, niezawodnymi rozwiązaniami płatniczymi",
      security: {
        title: "Bezpieczeństwo na poziomie bankowym",
        description: "Zgodność z PCI DSS Level 1 z szyfrowaniem end-to-end. Pełna zgodność z GDPR i gotowość na PSD2."
      },
      settlement: {
        title: "Rozliczenia następnego dnia",
        description: "Otrzymuj pieniądze szybciej dzięki integracji SEPA i natychmiastowym powiadomieniom w całej strefie euro."
      },
      support: {
        title: "24/7 Europejskie wsparcie",
        description: "Prawdziwi ludzie, prawdziwe rozwiązania. Nasz wielojęzyczny europejski zespół wsparcia jest zawsze do pomocy."
      }
    },
    testimonials: {
      title: "Zaufane przez europejskie firmy",
      subtitle: "Od małych sklepów po rozwijające się przedsiębiorstwa - firmy w całej Europie oszczędzają z CentrixPay",
      imageAlt: "Zadowoleni europejscy właściciele firm",
      imageCaption: "Budujemy sukces razem - europejscy przedsiębiorcy rozwijają się dzięki usprawnionymi rozwiązaniom płatniczym",
      reviews: [
        {
          name: "Anna Novotná",
          role: "Właścicielka kawiarni, Praga, CZ",
          quote: "CentrixPay zaoszczędził nam ponad 3000 € w pierwszym roku. Przejrzystość jest niesamowita.",
          image: "testimonial1"
        },
        {
          name: "Markus Weber",
          role: "Właściciel warsztatu samochodowego, Berlin DE",
          quote: "Rozliczenia następnego dnia zrewolucjonizowały nasze zarządzanie przepływem gotówki.",
          image: "testimonial2"
        },
        {
          name: "Carlos García",
          role: "Właściciel sklepu elektronicznego, Madryt ES",
          quote: "Najlepsza decyzja dla naszej firmy. Bez ukrytych opłat, tylko uczciwe ceny.",
          image: "testimonial3"
        }
      ]
    },
    cta: {
      title: "Gotowy zacząć oszczędzać?",
      subtitle: "Dołącz do setek europejskich firm, które już przeszły na przejrzyste, uczciwe ceny i zaoszczędziły tysiące.",
      button1: "Zacznij już dziś",
      button2: "Zobacz cennik"
    },
    footer: {
      description: "Profesjonalne przetwarzanie płatności dla europejskich firm. Zintegrowane z SEPA, zgodne z PSD2, bezpieczne GDPR.",
      services: "Usługi",
      company: "Firma",
      legal: "Prawne",
      about: "O nas",
      careers: "Kariera",
      blog: "Blog",
      privacy: "Polityka prywatności",
      terms: "Warunki użytkowania",
      cookies: "Polityka cookies",
      contact: "Kontakt",
      copyright: "© 2025 CentrixPay. Wszelkie prawa zastrzeżone."
    },
    services: {
      hero: {
        title: "Rozwiązania płatnicze dla Europy",
        subtitle: "Kompleksowe przetwarzanie płatności zaprojektowane dla unikalnych potrzeb europejskich firm."
      },
      cards: [
        {
          title: "Przetwarzanie kart",
          description: "Akceptuj wszystkie główne karty kredytowe i debetowe z konkurencyjnymi cenami interchange-plus. Visa, Mastercard, Maestro i więcej.",
          icon: "CreditCard"
        },
        {
          title: "Płatności SEPA",
          description: "Szybkie i bezpieczne polecenia zapłaty i przelewy SEPA dla płynnych transakcji w euro w 36 krajach.",
          icon: "Building"
        },
        {
          title: "Wielowalutowość",
          description: "Przetwarzaj płatności w EUR, CZK, PLN i więcej. Dynamiczna konwersja walut dla klientów międzynarodowych.",
          icon: "Globe"
        },
        {
          title: "Integracja e-commerce",
          description: "Płynna integracja z Twoim sklepem online. Wtyczki dla Shopify, WooCommerce, Magento i własne API.",
          icon: "ShoppingCart"
        },
        {
          title: "Terminale POS",
          description: "Nowoczesne terminale płatnicze dla sklepów stacjonarnych. Obsługa zbliżeniowa, chipowa i PIN.",
          icon: "Smartphone"
        },
        {
          title: "Płatności cykliczne",
          description: "Zarządzanie subskrypcjami i rozwiązania płatności cyklicznych dla firm SaaS i członkowskich.",
          icon: "RefreshCw"
        }
      ],
      compliance: {
        title: "Wbudowana zgodność europejska",
        items: [
          { title: "PSD2 Ready", description: "Zgodność z silnym uwierzytelnianiem klienta (SCA)" },
          { title: "GDPR Secure", description: "Pełna zgodność z ochroną danych i prywatności" },
          { title: "PCI DSS Level 1", description: "Najwyższy poziom certyfikacji bezpieczeństwa płatności" }
        ]
      }
    },
    pricing: {
      hero: {
        title: "Ceny dopasowane do Ciebie",
        subtitle: "Transparentne, konkurencyjne stawki dostosowane do Twojej firmy. Bez ukrytych opłat, bez niespodzianek."
      },
      getQuote: "Uzyskaj indywidualną wycenę",
      quoteCard: {
        customQuote: "Indywidualna wycena",
        tailoredForYou: "DOPASOWANE DO CIEBIE",
        transparentPricing: "Przejrzyste ceny",
        nextDaySettlements: "Rozliczenia następnego dnia",
        dedicatedSupport: "Dedykowane wsparcie",
        hiddenFees: "Ukryte opłaty",
        included: "W cenie",
        yourRate: "Twoja stawka",
        competitive: "Konkurencyjna",
        basedOnVolume: "Na podstawie Twojego wolumenu",
        lowerFees: "Niższe opłaty"
      },
      valueProps: {
        title: "Dlaczego firmy nas wybierają",
        subtitle: "Wierzymy w zdobywanie Twojego biznesu przez wartość, nie przez wiązanie umowami.",
        items: [
          { title: "Pełna przejrzystość", description: "Dokładnie widzisz, za co płacisz. Każda opłata jest wyraźnie wymieniona na zestawieniu – bez niespodzianek." },
          { title: "Konkurencyjne stawki", description: "Współpracujemy z Tobą, aby znaleźć najlepszą stawkę dla Twojego wolumenu i branży. Uczciwe ceny, zawsze." },
          { title: "Brak ukrytych opłat", description: "Bez opłat za konfigurację, bez opłat za anulowanie, bez miesięcznych minimów. Po prostu proste przetwarzanie płatności." },
          { title: "Szybkie rozliczenia", description: "Otrzymuj pieniądze, kiedy ich potrzebujesz. Dostępne opcje rozliczeń następnego dnia i tego samego dnia." },
          { title: "Prosta integracja", description: "Łatwa konfiguracja z istniejącymi systemami. My zajmujemy się złożonością techniczną." },
          { title: "Dedykowane wsparcie", description: "Prawdziwi ludzie, którzy rozumieją Twój biznes. Europejski zespół wsparcia dostępny, gdy go potrzebujesz." }
        ]
      },
      comparison: {
        title: "Czym się wyróżniamy",
        subtitle: "Zobacz, jak CentrixPay wypada w porównaniu z tradycyjnymi procesorami płatności.",
        feature: "Funkcja",
        traditional: "Tradycyjne",
        features: [
          "Przejrzysta struktura opłat",
          "Brak długoterminowych umów",
          "Rozliczenia następnego dnia",
          "Brak ukrytych opłat miesięcznych",
          "Dedykowany opiekun konta",
          "Obsługa wielu walut",
          "Zgodność z PSD2 i GDPR",
          "24/7 europejskie wsparcie"
        ]
      },
      ctaSection: {
        title: "Gotowy zobaczyć, ile możesz zaoszczędzić?",
        subtitle: "Uzyskaj spersonalizowaną wycenę na podstawie potrzeb Twojej firmy. Bez zobowiązań, bez presji – tylko uczciwe liczby.",
        callUs: "Lub zadzwoń do nas bezpośrednio"
      },
      plans: [
        {
          name: "Starter",
          price: "Proste",
          priceNote: "Przejrzyste ceny",
          description: "Idealne dla małych firm gotowych przyjmować płatności",
          features: [
            "Wszystkie główne karty",
            "Płatności SEPA w cenie",
            "Standardowy terminal POS",
            "Wsparcie e-mail",
            "Miesięczne zestawienia"
          ],
          cta: "Rozpocznij",
          popular: false
        },
        {
          name: "Professional",
          price: "Zoptymalizowane",
          priceNote: "Najlepsza wartość dla rozwijających się firm",
          description: "Idealny dla rozwijających się firm z wyższymi wolumenami",
          features: [
            "Wszystko ze Starter",
            "Rozliczenia następnego dnia",
            "Zaawansowany terminal POS",
            "Priorytetowe wsparcie telefoniczne",
            "Analityka w czasie rzeczywistym",
            "Przetwarzanie wielowalutowe"
          ],
          cta: "Najpopularniejszy",
          popular: true
        },
        {
          name: "Enterprise",
          price: "Indywidualnie",
          priceNote: "Dostosowane do Twoich potrzeb",
          description: "Dla dużych firm z wysokimi wolumenami transakcji",
          features: [
            "Wszystko z Professional",
            "Rozliczenia tego samego dnia",
            "Dedykowany opiekun konta",
            "Indywidualne integracje",
            "Gwarancja SLA",
            "Opcje white-label"
          ],
          cta: "Skontaktuj się ze sprzedażą",
          popular: false
        }
      ],
      faq: {
        title: "Często zadawane pytania",
        items: [
          {
            question: "Czym jest cennik interchange-plus?",
            answer: "Cennik interchange-plus oznacza, że płacisz rzeczywistą opłatę sieci kartowej (interchange) plus małą, przejrzystą marżę. To najbardziej uczciwy model cenowy w branży."
          },
          {
            question: "Czy są jakieś ukryte opłaty?",
            answer: "Absolutnie nie. Wierzymy w pełną przejrzystość. Każdą opłatę zobaczysz wyraźnie na miesięcznym zestawieniu. Bez niespodzianek."
          },
          {
            question: "Jak szybkie są rozliczenia?",
            answer: "Standardowe rozliczenia to T+2 dni robocze. Plany Professional mają rozliczenia następnego dnia, a plany Enterprise mogą otrzymać rozliczenia tego samego dnia."
          },
          {
            question: "Czy obsługujecie płatności cykliczne?",
            answer: "Tak! Wszystkie plany obejmują obsługę płatności cyklicznych i zarządzanie subskrypcjami. Idealne dla firm SaaS i członkowskich."
          }
        ]
      }
    },
    contact: {
      hero: {
        title: "Porozmawiajmy o Twojej firmie",
        subtitle: "Uzyskaj bezpłatną konsultację i zobacz, ile możesz zaoszczędzić z CentrixPay."
      },
      form: {
        name: "Imię i nazwisko",
        namePlaceholder: "Wpisz swoje imię i nazwisko",
        email: "Adres e-mail",
        emailPlaceholder: "twoj@email.pl",
        company: "Nazwa firmy",
        companyPlaceholder: "Nazwa Twojej firmy",
        country: "Kraj",
        countryPlaceholder: "Wybierz swój kraj",
        volume: "Miesięczny wolumen transakcji",
        volumePlaceholder: "Wybierz zakres wolumenu",
        message: "Wiadomość",
        messagePlaceholder: "Opowiedz nam o swojej firmie i potrzebach płatniczych...",
        submit: "Wyślij wiadomość",
        submitting: "Wysyłanie...",
        success: "Dziękujemy! Skontaktujemy się w ciągu 24 godzin.",
        error: "Coś poszło nie tak. Spróbuj ponownie."
      },
      countries: [
        "Czechy",
        "Niemcy",
        "Polska",
        "Hiszpania",
        "Portugalia",
        "Austria",
        "Holandia",
        "Belgia",
        "Francja",
        "Włochy",
        "Inny kraj UE"
      ],
      volumes: [
        "0 € - 10 000 €",
        "10 000 € - 50 000 €",
        "50 000 € - 100 000 €",
        "100 000 € - 500 000 €",
        "500 000 €+"
      ],
      info: {
        title: "Dlaczego wybrać CentrixPay?",
        items: [
          { title: "Oszczędź do 40%", description: "Nasi klienci zazwyczaj oszczędzają 30-40% na opłatach za przetwarzanie płatności." },
          { title: "Szybka konfiguracja", description: "Zacznij w zaledwie 48 godzin dzięki naszemu sprawnonemu onboardingowi." },
          { title: "Bez umów", description: "Miesięczne rozliczenia bez długoterminowych zobowiązań." }
        ]
      }
    }
  },
  es: {
    nav: {
      home: "Inicio",
      services: "Servicios",
      pricing: "Precios",
      contact: "Contacto",
      bookCall: "Agendar llamada"
    },
    hero: {
      badge: "Confianza en toda Europa",
      title: "AHORRA en procesamiento de pagos",
      subtitle: "Únete a cientos de empresas europeas que reducen las comisiones de pago con precios transparentes y una mejor experiencia de pago. Sin comisiones ocultas. Sin sorpresas.",
      cta1: "Agendar llamada",
      cta2: "Ver servicios",
      cardAmount: "€247,83",
      currency: "EUR",
      connected: "Conectado",
      cardNumber: "•••• •••• •••• 1234",
      expires: "12/25",
      processing: "Procesando...",
      approved: "Aprobado",
      settled: "Liquidado"
    },
    features: {
      title: "Por qué las empresas europeas eligen CentrixPay",
      subtitle: "Experimenta el futuro del procesamiento de pagos con nuestras soluciones innovadoras diseñadas específicamente para el mercado europeo.",
      imageAlt: "Empresario europeo dando la bienvenida a clientes",
      imageCaption: "Apoyando a emprendedores europeos con soluciones de pago transparentes y confiables",
      security: {
        title: "Seguridad de nivel bancario",
        description: "Cumplimiento PCI DSS Nivel 1 con cifrado de extremo a extremo. Cumplimiento total de GDPR y preparado para PSD2."
      },
      settlement: {
        title: "Liquidaciones al día siguiente",
        description: "Recibe tu dinero más rápido con integración SEPA y notificaciones instantáneas en toda la zona euro."
      },
      support: {
        title: "Soporte europeo 24/7",
        description: "Personas reales, soluciones reales. Nuestro equipo de soporte europeo multilingüe siempre está aquí para ayudar."
      }
    },
    testimonials: {
      title: "Confianza de empresas europeas",
      subtitle: "Desde pequeños comerciantes hasta empresas en crecimiento, negocios de toda Europa ahorran con CentrixPay",
      imageAlt: "Empresarios europeos satisfechos",
      imageCaption: "Construyendo el éxito juntos - Emprendedores europeos prosperando con soluciones de pago optimizadas",
      reviews: [
        {
          name: "Anna Novotná",
          role: "Propietaria, Cafetería en Praga, CZ",
          quote: "CentrixPay nos ahorró más de €3.000 en nuestro primer año. La transparencia es increíble.",
          image: "testimonial1"
        },
        {
          name: "Markus Weber",
          role: "Propietario, Taller de reparación, Berlín DE",
          quote: "Las liquidaciones al día siguiente han revolucionado nuestra gestión de flujo de caja.",
          image: "testimonial2"
        },
        {
          name: "Carlos García",
          role: "Propietario, Tienda de electrónica, Madrid ES",
          quote: "La mejor decisión para nuestro negocio. Sin comisiones ocultas, solo precios honestos.",
          image: "testimonial3"
        }
      ]
    },
    cta: {
      title: "¿Listo para empezar a ahorrar?",
      subtitle: "Únete a cientos de empresas europeas que ya han cambiado a precios transparentes y justos y han ahorrado miles.",
      button1: "Empieza hoy",
      button2: "Ver precios"
    },
    footer: {
      description: "Procesamiento de pagos premium para empresas europeas. Integrado con SEPA, compatible con PSD2, seguro con GDPR.",
      services: "Servicios",
      company: "Empresa",
      legal: "Legal",
      about: "Sobre nosotros",
      careers: "Carreras",
      blog: "Blog",
      privacy: "Política de privacidad",
      terms: "Términos de servicio",
      cookies: "Política de cookies",
      contact: "Contacto",
      copyright: "© 2025 CentrixPay. Todos los derechos reservados."
    },
    services: {
      hero: {
        title: "Soluciones de pago para Europa",
        subtitle: "Procesamiento de pagos integral diseñado para las necesidades únicas de las empresas europeas."
      },
      cards: [
        {
          title: "Procesamiento de tarjetas",
          description: "Acepta todas las principales tarjetas de crédito y débito con precios interchange-plus competitivos. Visa, Mastercard, Maestro y más.",
          icon: "CreditCard"
        },
        {
          title: "Pagos SEPA",
          description: "Adeudos directos y transferencias SEPA rápidas y seguras para transacciones en euros fluidas en 36 países.",
          icon: "Building"
        },
        {
          title: "Multi-moneda",
          description: "Procesa pagos en EUR, CZK, PLN y más. Conversión dinámica de divisas para clientes internacionales.",
          icon: "Globe"
        },
        {
          title: "Integración e-commerce",
          description: "Integración perfecta con tu tienda online. Plugins para Shopify, WooCommerce, Magento y APIs personalizadas.",
          icon: "ShoppingCart"
        },
        {
          title: "Terminales POS",
          description: "Terminales de pago modernos para tiendas físicas. Contactless, chip y PIN soportados.",
          icon: "Smartphone"
        },
        {
          title: "Pagos recurrentes",
          description: "Gestión de suscripciones y soluciones de pagos recurrentes para negocios SaaS y de membresía.",
          icon: "RefreshCw"
        }
      ],
      compliance: {
        title: "Cumplimiento europeo incorporado",
        items: [
          { title: "PSD2 Ready", description: "Compatible con autenticación fuerte del cliente (SCA)" },
          { title: "GDPR Secure", description: "Cumplimiento total de protección de datos y privacidad" },
          { title: "PCI DSS Level 1", description: "Certificación de seguridad de pagos de más alto nivel" }
        ]
      }
    },
    pricing: {
      hero: {
        title: "Precios que funcionan para ti",
        subtitle: "Tarifas transparentes y competitivas adaptadas a tu negocio. Sin comisiones ocultas, sin sorpresas."
      },
      getQuote: "Obtén tu presupuesto personalizado",
      quoteCard: {
        customQuote: "Presupuesto personalizado",
        tailoredForYou: "ADAPTADO PARA TI",
        transparentPricing: "Precios transparentes",
        nextDaySettlements: "Liquidaciones al día siguiente",
        dedicatedSupport: "Soporte dedicado",
        hiddenFees: "Comisiones ocultas",
        included: "Incluido",
        yourRate: "Tu tarifa",
        competitive: "Competitiva",
        basedOnVolume: "Según tu volumen",
        lowerFees: "Comisiones más bajas"
      },
      valueProps: {
        title: "Por qué las empresas nos eligen",
        subtitle: "Creemos en ganar tu negocio a través del valor, no atándote a contratos.",
        items: [
          { title: "Transparencia completa", description: "Ve exactamente por qué pagas. Cada comisión está claramente listada en tu extracto—sin sorpresas." },
          { title: "Tarifas competitivas", description: "Trabajamos contigo para encontrar la mejor tarifa para tu volumen e industria. Precios justos, siempre." },
          { title: "Sin comisiones ocultas", description: "Sin comisiones de configuración, sin comisiones de cancelación, sin mínimos mensuales. Solo procesamiento de pagos directo." },
          { title: "Liquidaciones rápidas", description: "Recibe tu dinero cuando lo necesites. Opciones de liquidación al día siguiente y el mismo día disponibles." },
          { title: "Integración simple", description: "Configuración fácil con tus sistemas existentes. Nosotros manejamos la complejidad técnica." },
          { title: "Soporte dedicado", description: "Personas reales que entienden tu negocio. Equipo de soporte europeo disponible cuando lo necesites." }
        ]
      },
      comparison: {
        title: "Cómo nos diferenciamos",
        subtitle: "Ve cómo CentrixPay se compara con los procesadores de pago tradicionales.",
        feature: "Característica",
        traditional: "Tradicional",
        features: [
          "Estructura de tarifas transparente",
          "Sin contratos a largo plazo",
          "Liquidaciones al día siguiente",
          "Sin comisiones mensuales ocultas",
          "Gestor de cuenta dedicado",
          "Soporte multi-moneda",
          "Cumplimiento PSD2 y GDPR",
          "Soporte europeo 24/7"
        ]
      },
      ctaSection: {
        title: "¿Listo para ver cuánto podrías ahorrar?",
        subtitle: "Obtén un presupuesto personalizado basado en las necesidades de tu negocio. Sin compromiso, sin presión—solo números honestos.",
        callUs: "O llámanos directamente"
      },
      plans: [
        {
          name: "Starter",
          price: "Simple",
          priceNote: "Precios directos",
          description: "Perfecto para pequeñas empresas listas para aceptar pagos",
          features: [
            "Todas las tarjetas principales",
            "Pagos SEPA incluidos",
            "Terminal POS estándar",
            "Soporte por email",
            "Estados de cuenta mensuales"
          ],
          cta: "Comenzar",
          popular: false
        },
        {
          name: "Professional",
          price: "Optimizado",
          priceNote: "Mejor valor para empresas en crecimiento",
          description: "Ideal para empresas en crecimiento con mayores volúmenes",
          features: [
            "Todo en Starter",
            "Liquidaciones al día siguiente",
            "Terminal POS avanzado",
            "Soporte telefónico prioritario",
            "Analíticas en tiempo real",
            "Procesamiento multi-moneda"
          ],
          cta: "Más popular",
          popular: true
        },
        {
          name: "Enterprise",
          price: "Personalizado",
          priceNote: "Adaptado a tus necesidades",
          description: "Para grandes empresas con altos volúmenes de transacciones",
          features: [
            "Todo en Professional",
            "Liquidaciones el mismo día",
            "Gestor de cuenta dedicado",
            "Integraciones personalizadas",
            "Garantía SLA",
            "Opciones white-label"
          ],
          cta: "Contactar ventas",
          popular: false
        }
      ],
      faq: {
        title: "Preguntas frecuentes",
        items: [
          {
            question: "¿Qué es el precio interchange-plus?",
            answer: "El precio interchange-plus significa que pagas la tarifa real de la red de tarjetas (interchange) más un pequeño y transparente margen. Es el modelo de precios más honesto de la industria."
          },
          {
            question: "¿Hay comisiones ocultas?",
            answer: "Absolutamente no. Creemos en la transparencia total. Verás cada comisión claramente en tu estado de cuenta mensual. Sin sorpresas."
          },
          {
            question: "¿Qué tan rápidas son las liquidaciones?",
            answer: "Las liquidaciones estándar son T+2 días hábiles. Los planes Professional obtienen liquidaciones al día siguiente, y los planes Enterprise pueden recibir liquidaciones el mismo día."
          },
          {
            question: "¿Soportan pagos recurrentes?",
            answer: "¡Sí! Todos los planes incluyen soporte para facturación recurrente y gestión de suscripciones. Perfecto para negocios SaaS y de membresía."
          }
        ]
      }
    },
    contact: {
      hero: {
        title: "Hablemos de tu negocio",
        subtitle: "Obtén una consulta gratuita y descubre cuánto podrías ahorrar con CentrixPay."
      },
      form: {
        name: "Nombre completo",
        namePlaceholder: "Ingresa tu nombre completo",
        email: "Correo electrónico",
        emailPlaceholder: "tu@email.com",
        company: "Nombre de la empresa",
        companyPlaceholder: "Nombre de tu empresa",
        country: "País",
        countryPlaceholder: "Selecciona tu país",
        volume: "Volumen mensual de transacciones",
        volumePlaceholder: "Selecciona rango de volumen",
        message: "Mensaje",
        messagePlaceholder: "Cuéntanos sobre tu negocio y necesidades de pago...",
        submit: "Enviar mensaje",
        submitting: "Enviando...",
        success: "¡Gracias! Nos pondremos en contacto en 24 horas.",
        error: "Algo salió mal. Por favor intenta de nuevo."
      },
      countries: [
        "República Checa",
        "Alemania",
        "Polonia",
        "España",
        "Portugal",
        "Austria",
        "Países Bajos",
        "Bélgica",
        "Francia",
        "Italia",
        "Otro país de la UE"
      ],
      volumes: [
        "€0 - €10.000",
        "€10.000 - €50.000",
        "€50.000 - €100.000",
        "€100.000 - €500.000",
        "€500.000+"
      ],
      info: {
        title: "¿Por qué elegir CentrixPay?",
        items: [
          { title: "Ahorra hasta 40%", description: "Nuestros clientes típicamente ahorran 30-40% en comisiones de procesamiento de pagos." },
          { title: "Configuración rápida", description: "Comienza en tan solo 48 horas con nuestro onboarding optimizado." },
          { title: "Sin contratos", description: "Facturación mensual sin compromisos a largo plazo." }
        ]
      }
    }
  },
  pt: {
    nav: {
      home: "Início",
      services: "Serviços",
      pricing: "Preços",
      contact: "Contacto",
      bookCall: "Agendar chamada"
    },
    hero: {
      badge: "Confiança em toda a Europa",
      title: "POUPE no processamento de pagamentos",
      subtitle: "Junte-se a centenas de empresas europeias que reduzem as taxas de pagamento com preços transparentes e uma melhor experiência de pagamento. Sem taxas ocultas. Sem surpresas.",
      cta1: "Agendar chamada",
      cta2: "Ver serviços",
      cardAmount: "€247,83",
      currency: "EUR",
      connected: "Conectado",
      cardNumber: "•••• •••• •••• 1234",
      expires: "12/25",
      processing: "A processar...",
      approved: "Aprovado",
      settled: "Liquidado"
    },
    features: {
      title: "Porque é que as empresas europeias escolhem a CentrixPay",
      subtitle: "Experimente o futuro do processamento de pagamentos com as nossas soluções inovadoras desenhadas especificamente para o mercado europeu.",
      imageAlt: "Empresário europeu a receber clientes",
      imageCaption: "A apoiar empreendedores europeus com soluções de pagamento transparentes e fiáveis",
      security: {
        title: "Segurança de nível bancário",
        description: "Conformidade PCI DSS Nível 1 com encriptação ponto-a-ponto. Conformidade total com GDPR e pronto para PSD2."
      },
      settlement: {
        title: "Liquidações no dia seguinte",
        description: "Receba o seu dinheiro mais rápido com integração SEPA e notificações instantâneas em toda a zona euro."
      },
      support: {
        title: "Suporte europeu 24/7",
        description: "Pessoas reais, soluções reais. A nossa equipa de suporte europeia multilingue está sempre aqui para ajudar."
      }
    },
    testimonials: {
      title: "Confiança de empresas europeias",
      subtitle: "Desde pequenos retalhistas a empresas em crescimento, negócios de toda a Europa poupam com a CentrixPay",
      imageAlt: "Empresários europeus satisfeitos",
      imageCaption: "A construir o sucesso juntos - Empreendedores europeus a prosperar com soluções de pagamento otimizadas",
      reviews: [
        {
          name: "Anna Novotná",
          role: "Proprietária, Café em Praga, CZ",
          quote: "A CentrixPay poupou-nos mais de €3.000 no primeiro ano. A transparência é incrível.",
          image: "testimonial1"
        },
        {
          name: "Markus Weber",
          role: "Proprietário, Oficina auto, Berlim DE",
          quote: "As liquidações no dia seguinte revolucionaram a nossa gestão de fluxo de caixa.",
          image: "testimonial2"
        },
        {
          name: "Carlos García",
          role: "Proprietário, Loja de eletrónica, Madrid ES",
          quote: "A melhor decisão para o nosso negócio. Sem taxas ocultas, apenas preços honestos.",
          image: "testimonial3"
        }
      ]
    },
    cta: {
      title: "Pronto para começar a poupar?",
      subtitle: "Junte-se a centenas de empresas europeias que já mudaram para preços transparentes e justos e pouparam milhares.",
      button1: "Comece hoje",
      button2: "Ver preços"
    },
    footer: {
      description: "Processamento de pagamentos premium para empresas europeias. Integrado com SEPA, compatível com PSD2, seguro com GDPR.",
      services: "Serviços",
      company: "Empresa",
      legal: "Legal",
      about: "Sobre nós",
      careers: "Carreiras",
      blog: "Blog",
      privacy: "Política de privacidade",
      terms: "Termos de serviço",
      cookies: "Política de cookies",
      contact: "Contacto",
      copyright: "© 2025 CentrixPay. Todos os direitos reservados."
    },
    services: {
      hero: {
        title: "Soluções de pagamento para a Europa",
        subtitle: "Processamento de pagamentos abrangente desenhado para as necessidades únicas das empresas europeias."
      },
      cards: [
        {
          title: "Processamento de cartões",
          description: "Aceite todos os principais cartões de crédito e débito com preços interchange-plus competitivos. Visa, Mastercard, Maestro e mais.",
          icon: "CreditCard"
        },
        {
          title: "Pagamentos SEPA",
          description: "Débitos diretos e transferências SEPA rápidas e seguras para transações em euros sem problemas em 36 países.",
          icon: "Building"
        },
        {
          title: "Multi-moeda",
          description: "Processe pagamentos em EUR, CZK, PLN e mais. Conversão dinâmica de moeda para clientes internacionais.",
          icon: "Globe"
        },
        {
          title: "Integração e-commerce",
          description: "Integração perfeita com a sua loja online. Plugins para Shopify, WooCommerce, Magento e APIs personalizadas.",
          icon: "ShoppingCart"
        },
        {
          title: "Terminais POS",
          description: "Terminais de pagamento modernos para lojas físicas. Contactless, chip e PIN suportados.",
          icon: "Smartphone"
        },
        {
          title: "Pagamentos recorrentes",
          description: "Gestão de subscrições e soluções de pagamentos recorrentes para negócios SaaS e de membros.",
          icon: "RefreshCw"
        }
      ],
      compliance: {
        title: "Conformidade europeia incorporada",
        items: [
          { title: "PSD2 Ready", description: "Compatível com autenticação forte do cliente (SCA)" },
          { title: "GDPR Secure", description: "Conformidade total de proteção de dados e privacidade" },
          { title: "PCI DSS Level 1", description: "Certificação de segurança de pagamentos de nível mais alto" }
        ]
      }
    },
    pricing: {
      hero: {
        title: "Preços que funcionam para si",
        subtitle: "Tarifas transparentes e competitivas adaptadas ao seu negócio. Sem taxas ocultas, sem surpresas."
      },
      getQuote: "Obtenha o seu orçamento personalizado",
      quoteCard: {
        customQuote: "Orçamento personalizado",
        tailoredForYou: "ADAPTADO PARA SI",
        transparentPricing: "Preços transparentes",
        nextDaySettlements: "Liquidações no dia seguinte",
        dedicatedSupport: "Suporte dedicado",
        hiddenFees: "Taxas ocultas",
        included: "Incluído",
        yourRate: "A sua tarifa",
        competitive: "Competitiva",
        basedOnVolume: "Com base no seu volume",
        lowerFees: "Taxas mais baixas"
      },
      valueProps: {
        title: "Porque é que as empresas nos escolhem",
        subtitle: "Acreditamos em conquistar o seu negócio através do valor, não prendendo-o a contratos.",
        items: [
          { title: "Transparência completa", description: "Veja exatamente pelo que está a pagar. Cada taxa está claramente listada no seu extrato—sem surpresas." },
          { title: "Tarifas competitivas", description: "Trabalhamos consigo para encontrar a melhor tarifa para o seu volume e indústria. Preços justos, sempre." },
          { title: "Sem taxas ocultas", description: "Sem taxas de configuração, sem taxas de cancelamento, sem mínimos mensais. Apenas processamento de pagamentos direto." },
          { title: "Liquidações rápidas", description: "Receba o seu dinheiro quando precisar. Opções de liquidação no dia seguinte e no mesmo dia disponíveis." },
          { title: "Integração simples", description: "Configuração fácil com os seus sistemas existentes. Nós tratamos da complexidade técnica." },
          { title: "Suporte dedicado", description: "Pessoas reais que entendem o seu negócio. Equipa de suporte europeia disponível quando precisar." }
        ]
      },
      comparison: {
        title: "Como somos diferentes",
        subtitle: "Veja como a CentrixPay se compara aos processadores de pagamento tradicionais.",
        feature: "Funcionalidade",
        traditional: "Tradicional",
        features: [
          "Estrutura de taxas transparente",
          "Sem contratos de longo prazo",
          "Liquidações no dia seguinte",
          "Sem taxas mensais ocultas",
          "Gestor de conta dedicado",
          "Suporte multi-moeda",
          "Conformidade PSD2 e GDPR",
          "Suporte europeu 24/7"
        ]
      },
      ctaSection: {
        title: "Pronto para ver quanto pode poupar?",
        subtitle: "Obtenha um orçamento personalizado com base nas necessidades do seu negócio. Sem compromisso, sem pressão—apenas números honestos.",
        callUs: "Ou ligue-nos diretamente"
      },
      plans: [
        {
          name: "Starter",
          price: "Simples",
          priceNote: "Preços diretos",
          description: "Perfeito para pequenas empresas prontas para aceitar pagamentos",
          features: [
            "Todos os cartões principais",
            "Pagamentos SEPA incluídos",
            "Terminal POS standard",
            "Suporte por email",
            "Extratos mensais"
          ],
          cta: "Começar",
          popular: false
        },
        {
          name: "Professional",
          price: "Otimizado",
          priceNote: "Melhor valor para empresas em crescimento",
          description: "Ideal para empresas em crescimento com volumes maiores",
          features: [
            "Tudo no Starter",
            "Liquidações no dia seguinte",
            "Terminal POS avançado",
            "Suporte telefónico prioritário",
            "Análises em tempo real",
            "Processamento multi-moeda"
          ],
          cta: "Mais popular",
          popular: true
        },
        {
          name: "Enterprise",
          price: "Personalizado",
          priceNote: "Adaptado às suas necessidades",
          description: "Para grandes empresas com altos volumes de transações",
          features: [
            "Tudo no Professional",
            "Liquidações no mesmo dia",
            "Gestor de conta dedicado",
            "Integrações personalizadas",
            "Garantia SLA",
            "Opções white-label"
          ],
          cta: "Contactar vendas",
          popular: false
        }
      ],
      faq: {
        title: "Perguntas frequentes",
        items: [
          {
            question: "O que é o preço interchange-plus?",
            answer: "O preço interchange-plus significa que paga a taxa real da rede de cartões (interchange) mais uma pequena e transparente margem. É o modelo de preços mais honesto da indústria."
          },
          {
            question: "Há taxas ocultas?",
            answer: "Absolutamente não. Acreditamos na transparência total. Verá cada taxa claramente no seu extrato mensal. Sem surpresas."
          },
          {
            question: "Quão rápidas são as liquidações?",
            answer: "As liquidações standard são T+2 dias úteis. Os planos Professional têm liquidações no dia seguinte, e os planos Enterprise podem receber liquidações no mesmo dia."
          },
          {
            question: "Suportam pagamentos recorrentes?",
            answer: "Sim! Todos os planos incluem suporte para faturação recorrente e gestão de subscrições. Perfeito para negócios SaaS e de membros."
          }
        ]
      }
    },
    contact: {
      hero: {
        title: "Vamos falar sobre o seu negócio",
        subtitle: "Obtenha uma consulta gratuita e veja quanto pode poupar com a CentrixPay."
      },
      form: {
        name: "Nome completo",
        namePlaceholder: "Introduza o seu nome completo",
        email: "Endereço de email",
        emailPlaceholder: "seu@email.pt",
        company: "Nome da empresa",
        companyPlaceholder: "Nome da sua empresa",
        country: "País",
        countryPlaceholder: "Selecione o seu país",
        volume: "Volume mensal de transações",
        volumePlaceholder: "Selecione intervalo de volume",
        message: "Mensagem",
        messagePlaceholder: "Conte-nos sobre o seu negócio e necessidades de pagamento...",
        submit: "Enviar mensagem",
        submitting: "A enviar...",
        success: "Obrigado! Entraremos em contacto em 24 horas.",
        error: "Algo correu mal. Por favor tente novamente."
      },
      countries: [
        "República Checa",
        "Alemanha",
        "Polónia",
        "Espanha",
        "Portugal",
        "Áustria",
        "Países Baixos",
        "Bélgica",
        "França",
        "Itália",
        "Outro país da UE"
      ],
      volumes: [
        "€0 - €10.000",
        "€10.000 - €50.000",
        "€50.000 - €100.000",
        "€100.000 - €500.000",
        "€500.000+"
      ],
      info: {
        title: "Porquê escolher a CentrixPay?",
        items: [
          { title: "Poupe até 40%", description: "Os nossos clientes tipicamente poupam 30-40% em taxas de processamento de pagamentos." },
          { title: "Configuração rápida", description: "Comece em apenas 48 horas com o nosso onboarding otimizado." },
          { title: "Sem contratos", description: "Faturação mensal sem compromissos a longo prazo." }
        ]
      }
    }
  }
};

export const languages = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'cs', name: 'Čeština', flag: '🇨🇿' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'pl', name: 'Polski', flag: '🇵🇱' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' }
];
